import { QUERY_KEYS } from '../../api/config';
import { health } from '../../api/Endpoints';
import { useQuery } from '@tanstack/react-query';
import {
  HealthZonesResponse,
  MyLongevityResponse,
  MicrobiomeProfileResponse,
  MyActiveMicrobesResponse,
  MicrobiomeDetailResponse,
  HealthConnectionsResponse,
  BiologicalAgeResponse,
  HealthConnectionsListResponse,
  CommonlyLinkedSymptomsResponse,
  IDoctorsNoteResponse,
  L4HealthConnectionListResponse,
  IScoreReferenceResponse,
  InflammagingDetailResponse,
  ConnectionDetailResponse,
  ConnectionDetailL4DetailResponse,
  BoosterAndBlockerSummaryResponse,
  IContributingScoreResponse,
  IScoreDetailResponse,
  IScoreLevel,
  HealthZoneDetailResponse,
  ImproveScoreSummary,
  ImproveScoreDetailResponse, ImproveScoreParams
} from './Health.model';

export const useGetHealthZonesSummary = () => {
  return useQuery<HealthZonesResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_ZONES_SUMMARY],
    queryFn: async () => {
      const response = await health.getHealthZonesSummary();
      return response.data;
    },
  });
};

export const useGetMyLongevity = () => {
  return useQuery<MyLongevityResponse, Error>({
    queryKey: [QUERY_KEYS.MY_LONGEVITY],
    queryFn: async () => {
      const response = await health.getMyLongevity();
      return response.data;
    },
  });
};

export const useGetMyMicrobiomeProfiles = () => {
  return useQuery<MicrobiomeProfileResponse, Error>({
    queryKey: [QUERY_KEYS.MY_MICROBIOME_PROFILES],
    queryFn: async () => {
      const response = await health.getMyMicrobiomeProfiles();
      return response.data;
    },
  });
};

export const useGetMyActiveMicrobes = (type: string) => {
  return useQuery<MyActiveMicrobesResponse, Error>({
    queryKey: [QUERY_KEYS.MY_ACTIVE_MICROBES, type],
    queryFn: async () => {
      const response = await health.getMyActiveMicrobes(type);
      return response.data;
    },
  });
};

export const useGetMyMicrobiomeDetail = (type: string) => {
  return useQuery<MicrobiomeDetailResponse, Error>({
    queryKey: [QUERY_KEYS.MICROBIOME_DETAIL, type],
    queryFn: async () => {
      const response = await health.getMyMicrobiomeDetail(type);
      return response.data;
    },
  });
};

export const useGetMyHealthConnectionsSummary = () => {
  return useQuery<HealthConnectionsResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_CONNECTIONS_SUMMARY],
    queryFn: async () => {
      const response = await health.getMyHealthConnectionsSummary();
      return response.data;
    },
  });
};

export const useGetMyBioAgeDetail = () => {
  return useQuery<BiologicalAgeResponse, Error>({
    queryKey: [QUERY_KEYS.BIO_AGE_DETAIL],
    queryFn: async () => {
      const response = await health.getMyBioAgeDetail();
      return response.data;
    },
  });
};

export const useGetMyHealthConnectionList = () => {
  return useQuery<HealthConnectionsListResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_CONNECTIONS_LIST],
    queryFn: async () => {
      const response = await health.getMyHealthConnectionList();
      return response.data;
    },
  });
};

export const useGetCommonlyLinkedSymptoms = (scoreId: number) => {
  return useQuery<CommonlyLinkedSymptomsResponse, Error>({
    queryKey: [QUERY_KEYS.COMMONLY_LINKED_SYMPTOMS, scoreId],
    queryFn: async () => {
      const response = await health.getCommonlyLinkedSymptoms(scoreId);
      return response.data;
    },
  });
};

export const useGetDoctorsNotes = (level: IScoreLevel, scoreId: number) => {
  return useQuery<IDoctorsNoteResponse, Error>({
    queryKey: [QUERY_KEYS.DOCTORS_NOTE, level, scoreId],
    queryFn: async () => {
      const response = await health.getDoctorsNotes(level, scoreId);
      return response.data;
    },
    enabled: !!(level && scoreId),
  });
};

export const useGetL4ConnectionList = (scoreId: number) => {
  return useQuery<L4HealthConnectionListResponse, Error>({
    queryKey: [QUERY_KEYS.L4_CONNECTION_LIST, scoreId],
    queryFn: async () => {
      const response = await health.getL4ConnectionList(scoreId);
      return response.data;
    },
  });
};

export const useGetScoreReference = (scoreId: number) => {
  return useQuery<IScoreReferenceResponse, Error>({
    queryKey: [QUERY_KEYS.SCORE_REFERENCE, scoreId],
    queryFn: async () => {
      const response = await health.getScoreReference(scoreId);
      return response.data;
    },
    enabled: !!scoreId,
  });
};

export const useGetInflammagingDetail = () => {
  return useQuery<InflammagingDetailResponse, Error>({
    queryKey: [QUERY_KEYS.INFLAMMAGING_DETAIL],
    queryFn: async () => {
      const response = await health.getInflammagingDetail();
      return response.data;
    },
  });
};

export const useGetHealthConnectionDetail = (conditionName: string) => {
  return useQuery<ConnectionDetailResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_CONNECTIONS_DETAIL, conditionName],
    queryFn: async () => {
      const response = await health.getHealthConnectionDetail(conditionName);
      return response.data;
    },
  });
};

export const useGetHealthConnectionL4Detail = (
  conditionName: string,
  scoreId: number,
) => {
  return useQuery<ConnectionDetailL4DetailResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_CONNECTIONS_L4_DETAIL, conditionName, scoreId],
    queryFn: async () => {
      const response = await health.getHealthConnectionL4Detail(
        conditionName,
        scoreId,
      );
      return response.data;
    },
  });
};

export const useGetHealthBoosterAndBlocker = (scoreId: number) => {
  return useQuery<BoosterAndBlockerSummaryResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_BOOSTER_AND_BLOCKER, scoreId],
    queryFn: async () => {
      const response = await health.getHealthBoosterAndBlocker(scoreId);
      return response.data;
    },
  });
};

export const useGetHealthZoneDetail = (scoreId: number) => {
  return useQuery<HealthZoneDetailResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_ZONE_DETAIL, scoreId],
    queryFn: async () => {
      const response = await health.getHealthZoneDetail(scoreId);
      return response.data;
    },
  });
};

export const useGetContributingScores = (
  level: IScoreLevel,
  scoreId?: number,
) => {
  return useQuery<IContributingScoreResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_CONTRIBUTING_SCORES, level, scoreId],
    queryFn: async () => {
      const response = await health.getContributingScores(level, scoreId);
      return response.data;
    },
    enabled: !!level,
  });
};
export const useGetScoreDetails = (level: IScoreLevel, scoreId: number) => {
  return useQuery<IScoreDetailResponse, Error>({
    queryKey: [QUERY_KEYS.HEALTH_SCORE_DETAILS, level, scoreId],
    queryFn: async () => {
      const response = await health.getScoreDetails(level, scoreId);
      return response.data;
    },
    enabled: !!(level && scoreId),
  });
};

export const useGetImproveScoreSummary = (scoreId: number) => {
  return useQuery<ImproveScoreSummary, Error>({
    queryKey: [QUERY_KEYS.IMPROVE_SCORE_SUMMARY, scoreId],
    queryFn: async () => {
      const response = await health.getImproveScoreSummary(scoreId);
      return response.data;
    },
  });
};

export const useGetImproveScoreDetail = (level: IScoreLevel, params: ImproveScoreParams) => {
  return useQuery<ImproveScoreDetailResponse, Error>({
    queryKey: [QUERY_KEYS.IMPROVE_SCORE_DETAIL, params, level],
    queryFn: async () => {
      const response = await health.getImproveScoreDetail(params, level);
      return response.data;
    },
  });
};
